import React, { useEffect, useState } from "react";
import { Lightbulb, Smile, ThumbsUp } from "lucide-react";
import { useAlerts } from "../../../../hooks/useAlerts";

import {
  defineRecommendationType,
  definePluralOrSingular,
  fetchDataFromFiles,
  randomInteger,
} from "../../components/Recommendations";
import { getTextEmotionColor, getEmotionColor } from "../../utils";
import { NoHelpfulPracticesFound } from "../../../StudentData/components/AlertComponents";
import {
  getWellnessActionDisplay,
  isOtherWellnessAction,
} from "@/utils/wellnessActions";

const EmotionPatternCard = ({
  date,
  value,
  timestamp,
  alert,
  name,
  emotionPercentage,
  timeLabel,
  filteredDataPoints,
  state,
}) => {
  const { markAlertAsRead, markAlertAsUnread } = useAlerts();
  const [isToggling, setIsToggling] = useState(false);

  const handleToggleReadStatus = async (alert) => {
    if (!alert?._id) return;

    try {
      setIsToggling(true);
      const alertId = alert._id.toString();

      if (alert.read) {
        markAlertAsUnread(alertId);
      } else {
        markAlertAsRead(alertId);
      }
    } catch (error) {
    } finally {
      setIsToggling(false);
    }
  };

  const getRecommendations = async (givenEmotion, state) => {
    try {
      // Get all values synchronously
      const type = defineRecommendationType();
      const emotion = givenEmotion;

      // Define weather plural or singular
      let isPlural = true;
      if (state.chosenStudent && state.chosenStudent != "All Students") {
        isPlural = false;
      }
      const plural_or_singular = definePluralOrSingular(isPlural);

      const considerations = await fetchConsiderations(
        emotion,
        plural_or_singular,
        "BoostAcademics",
      );

      return considerations;
    } catch (error) {
      return "--";
    }
  };

  const fetchConsiderations = async (
    argument_variable,
    plural_or_singular,
    currentTab,
  ) => {
    try {
      const data_details = await fetchDataFromFiles([
        currentTab,
        argument_variable,
        "Considerations",
        plural_or_singular,
      ]);
      if (data_details.status === "error") {
        return "--";
      }

      const data = data_details.data;
      if (!data.considerations || data.considerations.length === 0) {
        return "--";
      }

      const randomIndex = randomInteger(0, data.considerations.length - 1);
      const consideration = data.considerations[randomIndex];
      return consideration;
    } catch (error) {
      return "--";
    }
  };

  const [dashRecommendations, setDashRecommendations] = useState(null);

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        const dash_recommendations = await getRecommendations(value, state);
        setDashRecommendations(dash_recommendations);
      } catch (error) {}
    };

    fetchRecommendations();
  }, [value, state]);

  // Process filteredDataPoints to calculate helpful action statistics
  const processRecommendations = () => {
    if (!filteredDataPoints || !Array.isArray(filteredDataPoints)) return [];

    const emotions =
      typeof value === "string" ? value.split(",").map((e) => e.trim()) : [];

    const emotionData = filteredDataPoints.filter((point) =>
      emotions.includes(point.emotion),
    );

    const actionStats = {};

    emotionData.forEach((point) => {
      let action = point.action || "";
      if (isOtherWellnessAction(action)) {
        action = "Other";
      } else {
        action = getWellnessActionDisplay(action);
      }
      if (!actionStats[action]) {
        actionStats[action] = {
          total: 0,
          helpful: 0,
        };
      }
      actionStats[action].total++;
      if (point.helpful) {
        actionStats[action].helpful++;
      }
    });

    const recommendations = Object.entries(actionStats)
      .filter(([action, stats]) => stats.helpful > 0)
      .map(([action, stats]) => ({
        title: action,
        helpfulPercentage: Math.round((stats.helpful / stats.total) * 100),
        helpful: stats.helpful,
        total: stats.total,
      }))
      .sort((a, b) => b.helpfulPercentage - a.helpfulPercentage)
      .slice(0, 3);

    return recommendations;
  };

  const recommendations = processRecommendations();

  return (
    <div className="flex items-center w-full sm:items-start mb-4 sm:mb-5 relative sm:will-change-auto gap-3 sm:gap-4 md:gap-5">
      <div className="hidden lg:flex flex-row gap-2 sm:gap-3 items-center justify-center w-28 sm:w-32 md:w-36">
        <span className="text-gray-500 font-semibold text-sm sm:text-base md:text-lg mt-1 mb-1">
          {date}
        </span>
        <div className="flex flex-col items-center">
          {/* {!isFirst && <div className="w-1 h-16 bg-[#E0E3E8] z-0" />} */}
          <div className="w-0.5 h-24 sm:h-32 bg-[#E0E3E8] z-0" />
          <div className="bg-[#6366f1] rounded-full p-1 sm:p-2 flex items-center justify-center w-5 h-5 sm:w-7 sm:h-7 mb-1 mt-1">
            <Smile className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
          </div>
          <div className="w-0.5 h-24 sm:h-32 bg-[#E0E3E8] z-0" />
          {/* {!isLast && <div className="w-1 h-16 bg-[#E0E3E8] z-0" />} */}
        </div>
      </div>

      <div className="flex-1 w-full">
        <div
          className={`${
            alert?.read ? "border-gray-300" : "border-[#6366F1]"
          } ${getEmotionColor(value)}
        border shadow-sm rounded pt-3 sm:pt-4 mb-1 transition-colors duration-300`}
        >
          <div>
            <div
              className={`${
                alert?.read
                  ? "bg-gray-300 text-gray-600"
                  : "bg-[#6366f1] text-white"
              } 
              text-sm sm:text-base font-semibold w-full flex justify-between items-center mb-1 gap-2 py-2 sm:py-3 px-3 sm:px-5 md:px-8`}
            >
              <span className="font-semibold text-base sm:text-lg md:text-xl">
                {`${name} was `}
                <span
                // className={`${getTextEmotionColor(value)}`}
                >
                  {value.split(",").join(", ")}
                </span>
                {` ${emotionPercentage}% of the time ${timeLabel}`}
              </span>
              <button
                className={`hidden sm:flex text-xs sm:text-sm flex text-right transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  isToggling ? "opacity-60" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  handleToggleReadStatus(alert);
                }}
                disabled={isToggling}
              >
                {alert?.read ? "Mark as Unread" : "Mark as Read"}
              </button>
            </div>

            {/* Timestamp */}
            <div className="flex justify-between items-center px-3 py-2 sm:px-5 md:px-8">
              <span className="text-gray-500 text-sm md:text-base">
                {timestamp}
              </span>
              <button
                className={`sm:hidden text-xs flex text-right text-[#256AA6] transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  isToggling ? "opacity-60" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  handleToggleReadStatus(alert);
                }}
                disabled={isToggling}
              >
                {alert?.read ? "Mark as Unread" : "Mark as Read"}
              </button>
            </div>

            {/* What Helps section */}
            <div className="font-semibold text-sm sm:text-base md:text-lg px-3 sm:px-5 md:px-8 uppercase">{`What Helps ${name} When They Feel ${value
              .split(",")
              .join(", ")}`}</div>
            <div className="rounded bg-white border border-[#DDDDDD] m-3 sm:mx-8 sm:my-4 py-2 sm:py-3 px-3 sm:px-5">
              <div className="font-semibold text-base sm:text-lg md:text-xl mb-2">
                This is what helps
              </div>

              <div className="space-y-2 sm:space-y-3">
                {recommendations.length > 0 ? (
                  recommendations.map((rec, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-1 sm:py-2 border-b border-gray-200"
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div
                          className={`${getTextEmotionColor(
                            value,
                          )} ${getEmotionColor(
                            value,
                          )} rounded-full w-5 h-5 sm:w-7 sm:h-7 flex items-center justify-center font-semibold text-base sm:text-lg md:text-xl`}
                        >
                          {index + 1}
                        </div>
                        <span className=" text-sm sm:text-base md:text-lg font-semibold">
                          {rec.title}
                        </span>
                      </div>
                      <div className="flex flex-col items-center gap-1 sm:gap-2">
                        <div className="flex items-center gap-1">
                          <ThumbsUp
                            className={`w-3 h-3 sm:w-4 sm:h-4 ${getTextEmotionColor(
                              value,
                            )}`}
                          />
                          <span className="text-sm sm:text-base md:text-lg font-medium">
                            {rec.helpfulPercentage}% helpful
                          </span>
                        </div>
                        <span className="text-xs sm:text-sm text-gray-500">
                          {rec.helpful} out of {rec.total} times
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-gray-500 py-3 sm:py-4">
                    <NoHelpfulPracticesFound />
                  </div>
                )}

                {/* IDEA section */}
                {dashRecommendations && dashRecommendations !== "--" && (
                  <div
                    className={`flex items-start gap-3 p-3 ${getEmotionColor(
                      value,
                    )} rounded-lg mt-3 sm:mt-4`}
                  >
                    <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600 mt-1 shrink-0" />
                    <div>
                      <span className="font-semibold text-xs sm:text-sm text-700">
                        IDEA:
                      </span>
                      <span className="text-xs sm:text-sm text-gray-700 ml-1">
                        {dashRecommendations}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="text-sm sm:text-base md:text-lg font-semibold py-3 sm:py-4 px-3 sm:px-5 md:px-8 flex items-center gap-2 w-full border-t border-[#DDDDDD]">
            <Smile className="w-3 h-3 sm:w-4 sm:h-4" /> Emotion Pattern
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmotionPatternCard;
